import React, { useState, useEffect } from 'react';
import { Collapsible } from 'exoflex';
import { StyleSheet, Animated, View, TouchableOpacity } from 'react-native';

import { Icon, TextInput, Button, Text } from '../core-ui';
import { PROJECT_COLLAPSIBLE, TEXT_INPUT } from '../constants/colors';

export type ProjectData = {
  id: string;
  name: string;
  googleSheetsURL: string;
  clientEmail: string;
  clientPassword: string;
};

type ProjectCollapsibleProps = {
  projectData: ProjectData;
  onSaveChanges: (newProjectData: ProjectData) => void;
  onDeleteProject: () => void;
};

export function ProjectCollapsible(props: ProjectCollapsibleProps) {
  let { projectData, onDeleteProject, onSaveChanges } = props;

  let [projectName, setProjectName] = useState(projectData.name); // This is to prevent the collapsible title to change when typing project name.
  let [name, setName] = useState(projectData.name);
  let [googleSheetsURL, setGoogleSheetsURL] = useState(
    projectData.googleSheetsURL,
  );
  let [clientEmail, setClientEmail] = useState(projectData.clientEmail);
  let [clientPassword, setClientPassword] = useState(
    projectData.clientPassword,
  );

  let [isPasswordVisible, setPasswordVisibility] = useState(false);

  useEffect(() => {
    let { name, clientEmail, clientPassword, googleSheetsURL } = projectData;
    setProjectName(name);
    setName(name);
    setClientEmail(clientEmail);
    setClientPassword(clientPassword);
    setGoogleSheetsURL(googleSheetsURL);
  }, [projectData]);

  return (
    <Collapsible
      isCollapsed={projectData.id !== '-1'}
      title={projectName}
      style={styles.projectContainer}
      titleStyle={styles.headerText}
      titleContainerStyle={styles.projectTitleContainer}
      contentContainerStyle={styles.projectContentContainer}
      renderIconRight={(value) => (
        <Animated.View
          style={{
            transform: [
              {
                rotate: value.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['-90deg', '90deg'],
                }),
              },
            ],
          }}
        >
          <Icon
            name="chevronUpCircleOutline"
            color={PROJECT_COLLAPSIBLE.chevronUpCircleOutline}
          />
        </Animated.View>
      )}
    >
      <View style={styles.projectDataContainer}>
        <View style={styles.leftContentContainer}>
          <TextInput
            label="Project Name"
            value={name}
            onChangeText={setName}
            placeholder="Please enter project name"
            containerStyle={{ marginBottom: 10 }}
            errorMessage={
              !name && projectData.id !== '-1' ? "Field can't be empty" : null
            }
          />
          <TextInput
            label="Client's Email Address"
            value={clientEmail}
            onChangeText={setClientEmail}
            placeholder="Please enter client's email address"
            errorMessage={
              !clientEmail && projectData.id !== '-1'
                ? "Field can't be empty"
                : null
            }
          />
        </View>
        <View style={styles.rightContentContainer}>
          <TextInput
            label="Google Sheets Source URL"
            value={googleSheetsURL}
            onChangeText={setGoogleSheetsURL}
            placeholder="Please enter Google Sheets URL"
            containerStyle={{ marginBottom: 10 }}
            errorMessage={
              !googleSheetsURL && projectData.id !== '-1'
                ? "Field can't be empty"
                : null
            }
          />

          <View style={styles.passwordContainer}>
            <TextInput
              label="Client's Password"
              value={clientPassword}
              onChangeText={setClientPassword}
              secureTextEntry={!isPasswordVisible}
              containerStyle={{
                width: '80%',
              }}
              textInputStyle={{
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              }}
              placeholder="Please enter client's password"
              errorMessage={
                !clientPassword && projectData.id !== '-1'
                  ? "Field can't be empty"
                  : null
              }
            />
            <TouchableOpacity
              style={styles.showPasswordContainer}
              onPress={() => {
                setPasswordVisibility(!isPasswordVisible);
              }}
            >
              <Text
                style={[
                  styles.labelText,
                  { color: PROJECT_COLLAPSIBLE.showPasswordLabel },
                ]}
              >
                show
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      <View style={styles.projectFooterContainer}>
        {projectData.id === '-1' ? (
          <TouchableOpacity onPress={onDeleteProject}>
            <Text type="header" style={styles.cancelText}>
              Cancel
            </Text>
          </TouchableOpacity>
        ) : (
          <Button
            buttonMode="invisible"
            title="Delete Project"
            onPress={onDeleteProject}
            icon={() => (
              <Icon
                name="trash-can-outline"
                color={PROJECT_COLLAPSIBLE.deleteProjectText}
              />
            )}
            labelStyle={styles.deleteProjectText}
          />
        )}

        <Button
          title="Save Changes"
          onPress={() =>
            name &&
            clientEmail &&
            clientPassword &&
            googleSheetsURL &&
            onSaveChanges({
              ...projectData,
              name,
              googleSheetsURL,
              clientEmail,
              clientPassword,
            })
          }
          disabled={
            name && clientEmail && clientPassword && googleSheetsURL
              ? false
              : true
          }
          style={{
            padding: 0,
          }}
        />
      </View>
    </Collapsible>
  );
}

const styles = StyleSheet.create({
  headerText: {
    fontSize: 18,
    fontFamily: 'SourceSansPro-SemiBold',
  },
  labelText: {
    fontSize: 16,
    fontFamily: 'SourceSansPro-SemiBold',
  },
  projectTitleContainer: {
    paddingHorizontal: 24,
    paddingVertical: 28,
  },
  projectContainer: {
    borderRadius: 12,
    borderWidth: 0,
    marginBottom: 24,
    shadowOpacity: 0.15,
    shadowRadius: 2,
  },
  projectDataContainer: {
    flexDirection: 'row',
    width: '100%',
  },
  projectContentContainer: {
    padding: 24,
    paddingTop: 8,
  },
  leftContentContainer: {
    flex: 1,
    paddingRight: 12,
  },
  rightContentContainer: {
    flex: 1,
    paddingLeft: 12,
  },
  projectFooterContainer: {
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  passwordContainer: {
    flexDirection: 'row',
  },
  showPasswordContainer: {
    width: '20%',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
    borderColor: TEXT_INPUT.borderDefault,
    height: 50,
    marginTop: 26,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelText: {
    color: PROJECT_COLLAPSIBLE.deleteProjectText,
    fontSize: 17,
    letterSpacing: 0,
    margin: 16,
  },
  deleteProjectText: {
    color: PROJECT_COLLAPSIBLE.deleteProjectText,
    marginLeft: 12,
  },
});
